import type { Commands } from "@/api/commands";
import type { ParamSpec } from "@/api/types";

/** `strategy.info`: what a script declares about itself, and the engine settings that reproduce it. */
export type StrategyInfoResponse = {
  status?: "ok" | "invalid" | "unsupported" | string;
  error?: string;
  info?: {
    /** Backtest properties the script writes itself; the rest are Pine's defaults. */
    declared?: string[];
    backtest?: { calc_on_order_fills?: boolean; calc_on_every_tick?: boolean; use_bar_magnifier?: boolean };
  } | null;
  /** By engine: the parameters that make a backtest match the script's on TradingView. */
  engines?: Record<string, { config?: Record<string, string>; unsupported?: { property: string; setting: string; param?: string }[] }>;
};

type Request = Pick<Commands, "getStrategyInfo">;

/** Ask the server about a script (its text or a pinned script:// reference); null when it can't say. */
export async function fetchStrategyInfo(request: Request, source: string): Promise<StrategyInfoResponse | null> {
  if (!source.trim()) return null;
  try {
    const resp = (await request.getStrategyInfo({
      strategy_provider: "pinescript",
      strategy_config: { source },
    })) as StrategyInfoResponse | null;
    return resp?.status === "ok" ? resp : null;
  } catch {
    return null;
  }
}

/**
 * Engine settings a script's strategy() declaration calls for, typed for the
 * form: everything TradingView would use, declared or left at Pine's default
 * (1 contract, no commission, 1x leverage, fills at the next bar's open).
 * Only parameters the engine has are kept. Null without an answer.
 */
export function suggestedEngineConfig(
  resp: StrategyInfoResponse | null | undefined,
  specs: ParamSpec[],
  engine = "margin"
): Record<string, unknown> | null {
  const raw = resp?.engines?.[engine]?.config;
  if (!raw) return null;
  const byName = new Map(specs.map((s) => [s.name, s]));
  const out: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(raw)) {
    const spec = byName.get(name);
    if (specs.length > 0 && !spec) continue;
    if (spec?.type === "int" || spec?.type === "float") {
      const n = Number(value);
      if (Number.isFinite(n)) out[name] = n;
    } else if (spec?.type === "bool") {
      out[name] = value === "true";
    } else {
      out[name] = value;
    }
  }
  // An order size in cash or percent says nothing about the unit: spell it out
  // so a form that showed contracts before goes back to cash.
  if ("qty" in out && !("qty_unit" in out) && (specs.length === 0 || byName.has("qty_unit"))) out.qty_unit = "cash";
  if ("fee" in out && !("fee_unit" in out) && (specs.length === 0 || byName.has("fee_unit"))) out.fee_unit = "percent";
  return out;
}

/** Properties of the suggestion the engine can't reproduce, in plain words. */
export function unsupportedSettings(resp: StrategyInfoResponse | null | undefined, engine = "margin"): string[] {
  return (resp?.engines?.[engine]?.unsupported || []).map((u) => u.setting);
}
